import {
  BOT_TIERS,
  characterMods,
  DEFAULT_CHARACTER,
  GamePhase,
  hpRisk,
  isOnArena,
  isRuneId,
  kbResistAt,
  knockbackReadout,
  lavaMultAt,
  MoveState,
  obstacleDensityOf,
  resolveCharacter,
  ServerMessage,
  SLOT_KEYS,
  tintFor,
  type ArenaShape,
  type BotErrorEvent,
  type BotTier,
  type CharacterId,
  type FrictionPreset,
  type GameMode,
  type KillEvent,
  type MapId,
  type MapPropsMode,
  type MapMode,
  type MatchSettings,
  type Player,
  type RoundResultEvent,
  type RuneId,
  type SlotKey,
} from '@arena/shared';
import {
  NO_VITALS,
  pushKill,
  resetMatch,
  showBotError,
  useMatchStore,
  watchedId,
  type MatchSnapshot,
  type RacialView,
  type RosterEntry,
  type SelfVitals,
  type SpellSlotView,
} from '../store/matchStore';
import type { ArenaRoom } from './client';

/** Structural view of the synced state the bridge reads; lets tests pass plain objects. */
export interface StateLike {
  phase: string;
  phaseEndsAt: number;
  nextShrinkAt: number;
  arenaRadius: number;
  round: number;
  /** Teams mode rounds won, indexed by team; an ArraySchema on the wire. Absent before DROW-175. */
  teamRoundsWon?: Iterable<number>;
  hostId: string;
  settings: Omit<
    MatchSettings,
    | 'mode'
    | 'arenaShape'
    | 'frictionPreset'
    | 'mapMode'
    | 'mapPool'
    | 'startMapId'
    | 'mapProps'
    | 'obstacleDensity'
  > & {
    mode: string;
    arenaShape: string;
    frictionPreset: string;
    mapMode: string;
    mapPool: Iterable<string>;
    startMapId: string;
    mapProps: string;
    /** Absent or `''` from a server before DROW-138: reads as `normal`. */
    obstacleDensity?: string;
  };
  map: { mapId: string; arenaShape: string; lavaDpsPct: number };
  players: {
    values(): Iterable<SyncedPlayer>;
  };
}

/** A synced spell slot: `unlocked` is an ArraySchema on the wire, a plain array in tests. */
type SyncedSpellSlot = Omit<SpellSlotView, 'unlocked'> & { unlocked?: Iterable<string> };

type SyncedPlayer = PlayerLike & { spells: { get(slot: string): SyncedSpellSlot | undefined } };

/**
 * A synced player: the shared fields plus the seat's `botTier` (`''` = human), the Stone Guard
 * fields and the Emberward timer (absent = 0).
 */
type PlayerLike = Player & {
  botTier: string;
  racialCooldownEndsAt?: number;
  guardUntil?: number;
  wardUntil?: number;
};

/** Synced tier string → a known tier, or `''` for humans and anything unrecognised. */
export function botTierOf(raw: string): BotTier | '' {
  return (BOT_TIERS as readonly string[]).includes(raw) ? (raw as BotTier) : '';
}

function rosterEntry(p: SyncedPlayer): RosterEntry {
  return {
    sessionId: p.sessionId,
    name: p.name,
    botTier: botTierOf(p.botTier),
    character: resolveCharacter(p.character),
    colorIndex: p.colorIndex,
    team: p.team,
    ready: p.ready,
    shopReady: p.shopReady,
    connected: p.connected,
    alive: p.alive,
    hpPct: p.maxHp > 0 ? Math.ceil((p.hp / p.maxHp) * 100) : 0,
    gold: p.gold,
    points: p.points,
    kills: p.kills,
    deaths: p.deaths,
    damageDealt: p.damageDealt,
    runes: activeRunes(p),
  };
}

/** A player's active runes in slot order; unknown ids are dropped. */
function activeRunes(p: SyncedPlayer): RuneId[] {
  const runes: RuneId[] = [];
  for (const slot of SLOT_KEYS) {
    const rune = p.spells.get(slot)?.rune;
    if (isRuneId(rune)) runes.push(rune);
  }
  return runes;
}

function spellsOf(p: SyncedPlayer): Partial<Record<SlotKey, SpellSlotView>> {
  const spells: Partial<Record<SlotKey, SpellSlotView>> = {};
  for (const slot of SLOT_KEYS) {
    const s = p.spells.get(slot);
    if (!s) continue;
    spells[slot] = {
      spellId: s.spellId,
      level: s.level,
      cooldownEndsAt: s.cooldownEndsAt,
      rune: s.rune ?? '',
      // Copy out of the synced ArraySchema so the store holds plain data.
      unlocked: [...(s.unlocked ?? [])],
    };
  }
  return spells;
}

/** On lava during a fight (the lava damage test). */
function onLavaOf(p: PlayerLike, state: StateLike): boolean {
  return (
    state.phase === GamePhase.Fight &&
    p.alive &&
    !isOnArena(p.x, p.y, state.arenaRadius, state.map.arenaShape as ArenaShape)
  );
}

/**
 * The HUD's racial slot for one player: null (hidden) with the toggle off or no racial. A passive
 * is `active` while its condition holds (Fleetfoot on stone, Pactfire's free cast still unused);
 * the Space racials have a cooldown and a time they are up until (Stone Guard, Stealth).
 */
export function racialOf(p: PlayerLike, state: StateLike): RacialView | null {
  const def = characterMods(p.character, state.settings.racialAbilities).racial;
  if (!def) return null;
  let active = false;
  if (def.kind === 'passive') {
    if (def.surface?.stoneSpeedMult !== undefined && p.alive && !onLavaOf(p, state)) active = true;
    if (def.freeCastsPerRound && p.racialCharges > 0) active = true;
  }
  return {
    def,
    cooldownEndsAt: p.racialCooldownEndsAt ?? 0,
    guardUntil: p.guardUntil ?? 0,
    stealthUntil: p.stealthUntil ?? 0,
    active,
  };
}

/**
 * The local player's panel. Everything is coarse (whole HP, readout steps, booleans) so a
 * patch only re-renders it when something visible changed. `now` (server clock) decides whether
 * Emberward is up: its knockback resist and lava immunity show in the readouts.
 */
function vitalsOf(p: SyncedPlayer, state: StateLike, now: number): SelfVitals {
  return {
    alive: p.alive,
    hp: Math.ceil(p.hp),
    maxHp: Math.round(p.maxHp),
    kb: knockbackReadout(p.hp, p.maxHp, kbResistAt(p, now)),
    risk: hpRisk(p.hp, p.maxHp),
    sliding: p.moveState === MoveState.KnockedBack,
    onLava: onLavaOf(p, state),
    lavaDps: Math.round(p.maxHp * state.map.lavaDpsPct * lavaMultAt(p, now)),
    shieldUntil: p.shieldUntil,
    tetherEndsAt: p.tetherObstacle !== 0 || p.tetherTargetId !== '' ? p.tetherEndsAt : 0,
    burnUntil: p.burnUntil ?? 0,
    wardUntil: p.wardUntil ?? 0,
    stealthUntil: p.stealthUntil ?? 0,
  };
}

/**
 * Derive the UI-facing snapshot from synced state. Map order is join order. `spectatePick` is the
 * store's `spectateId`: who a dead local player would rather watch. `now` is the server clock.
 */
export function snapshotOf(
  state: StateLike,
  selfId: string,
  spectatePick: string | null = null,
  now = 0,
): MatchSnapshot {
  const roster: RosterEntry[] = [];
  let spells: MatchSnapshot['spells'] = {};
  let racial: RacialView | null = null;
  let self = NO_VITALS;
  const byId = new Map<string, SyncedPlayer>();
  for (const p of state.players.values()) {
    roster.push(rosterEntry(p));
    byId.set(p.sessionId, p);
    if (p.sessionId !== selfId) continue;
    spells = spellsOf(p);
    racial = racialOf(p, state);
    self = vitalsOf(p, state, now);
  }
  const phase = state.phase as GamePhase;
  const watchId = watchedId(phase, roster, selfId, spectatePick);
  const watchedPlayer = watchId ? byId.get(watchId) : undefined;
  const { settings } = state;
  return {
    phase,
    phaseEndsAt: state.phaseEndsAt,
    nextShrinkAt: state.nextShrinkAt,
    round: state.round,
    // Copy out of the synced ArraySchema so the store holds plain data.
    teamRoundsWon: [...(state.teamRoundsWon ?? [])],
    hostId: state.hostId,
    roster,
    self,
    watched: watchedPlayer
      ? {
          sessionId: watchedPlayer.sessionId,
          character: resolveCharacter(watchedPlayer.character),
          spells: spellsOf(watchedPlayer),
          racial: racialOf(watchedPlayer, state),
        }
      : null,
    spells,
    racial,
    settings: {
      ...settings,
      mode: settings.mode as GameMode,
      arenaShape: settings.arenaShape as ArenaShape,
      frictionPreset: settings.frictionPreset as FrictionPreset,
      mapMode: settings.mapMode as MapMode,
      // Copy out of the synced ArraySchema so the store holds plain data.
      mapPool: [...settings.mapPool] as MapId[],
      startMapId: settings.startMapId as MapId,
      mapProps: settings.mapProps as MapPropsMode,
      obstacleDensity: obstacleDensityOf(settings.obstacleDensity),
    },
    mapId: state.map.mapId as MapId,
  };
}

/** Structural equality for plain JSON-like values (snapshots are small; ~20 Hz). */
export function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  return ak.every((k) =>
    sameValue((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
  );
}

/**
 * Write the snapshot into the match store, keeping the previous object for every top-level field
 * that did not change, so zustand selectors only re-render on real changes.
 */
export function applySnapshot(next: MatchSnapshot): void {
  const current = useMatchStore.getState();
  const patch: Partial<MatchSnapshot> = {};
  for (const key of Object.keys(next) as (keyof MatchSnapshot)[]) {
    if (!sameValue(current[key], next[key])) Object.assign(patch, { [key]: next[key] });
  }
  if (Object.keys(patch).length > 0) useMatchStore.setState(patch);
}

function playerOf(room: ArenaRoom, sessionId: string | undefined): PlayerLike | undefined {
  return sessionId ? room.state.players.get(sessionId) : undefined;
}

/**
 * Kill-feed fields for one side of a kill; null when the player is unknown or absent. The team
 * colour is taken now so a row keeps it after the player leaves; FFA names stay uncoloured.
 */
function killSide(
  p: PlayerLike | undefined,
  mode: GameMode,
): { name: string; character: CharacterId; bot: boolean; tint: string | null } | null {
  return p
    ? {
        name: p.name,
        character: resolveCharacter(p.character),
        bot: botTierOf(p.botTier) !== '',
        tint: mode === 'teams' ? tintFor(p.colorIndex, p.team, mode) : null,
      }
    : null;
}

/**
 * Mirror the UI-facing slice of room state into zustand after every patch, and route transient
 * server messages into the HUD. Returns an unsubscribe function.
 */
export function bindMatch(room: ArenaRoom): () => void {
  resetMatch(room.sessionId);
  const sync = () => {
    // The root schema exists before the first full state is decoded; wait for its fields.
    if (room.state?.players && room.state.settings && room.state.map) {
      const pick = useMatchStore.getState().spectateId;
      applySnapshot(snapshotOf(room.state, room.sessionId, pick, room.clock.serverNow()));
    }
  };
  room.onStateChange(sync);
  sync();
  // A new spectating pick shows right away, not on the next patch.
  const offPick = useMatchStore.subscribe((s, prev) => {
    if (s.spectateId !== prev.spectateId) sync();
  });

  const offKill = room.onMessage(ServerMessage.Kill, (ev: KillEvent) => {
    const mode = room.state.settings.mode as GameMode;
    const victim = killSide(playerOf(room, ev.victimId), mode);
    const killer = killSide(playerOf(room, ev.killerId), mode);
    const assist = killSide(playerOf(room, ev.assistId), mode);
    // Dead: watch whoever got us first.
    if (ev.victimId === room.sessionId) {
      useMatchStore.setState({ spectateId: ev.killerId ?? null });
    }
    pushKill({
      victimName: victim?.name ?? '?',
      victimCharacter: victim?.character ?? DEFAULT_CHARACTER,
      victimBot: victim?.bot ?? false,
      victimTint: victim?.tint ?? null,
      killerName: killer?.name ?? null,
      killerCharacter: killer?.character ?? null,
      killerBot: killer?.bot ?? false,
      killerTint: killer?.tint ?? null,
      assistName: assist?.name ?? null,
      assistBot: assist?.bot ?? false,
      assistTint: assist?.tint ?? null,
      spellId: ev.spellId ?? null,
      lava: ev.cause === 'lava',
      mine: ev.victimId === room.sessionId || ev.killerId === room.sessionId,
      at: performance.now(),
    });
  });
  const offRound = room.onMessage(ServerMessage.RoundResult, (ev: RoundResultEvent) => {
    useMatchStore.setState({ lastRoundResult: ev });
  });
  const offBotError = room.onMessage(ServerMessage.BotError, (ev: BotErrorEvent) => {
    showBotError(ev.reason);
  });

  return () => {
    room.onStateChange.remove(sync);
    offPick();
    offKill();
    offRound();
    offBotError();
  };
}

/** Armed slot helper for the input layer; also used by the spell bar highlight. */
export function setArmedSlot(slot: SlotKey | null): void {
  if (useMatchStore.getState().armedSlot !== slot) useMatchStore.setState({ armedSlot: slot });
}

/** Flash a spell-bar cell (or the racial slot): a cast was tried on cooldown or on an empty slot. */
export function flashNotReady(slot: SlotKey | 'racial'): void {
  useMatchStore.setState((s) => ({ notReady: { slot, id: (s.notReady?.id ?? 0) + 1 } }));
}
